import { useMutation, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import { storeUserLanguage } from "../services/authService";
import {
  applyPreferredLanguageToUser,
  refetchAfterLanguageChange,
} from "../utils/refetchAfterLanguageChange";

export default function usePreferredLanguageToggle(profile = {}) {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();
  const currentPreferredLanguage = String(
    profile?.preferred_lang || user?.preferred_lang || user?.content_language || "EN"
  ).toUpperCase();
  const userId = profile?.id || profile?.user_id || user?.id || user?.user_id;

  const storeLanguageMutation = useMutation({
    mutationFn: storeUserLanguage,
    onSuccess: async (result, variables) => {
      const preferredLang = String(variables?.preferred_lang || "EN").toUpperCase();
      if (user) {
        updateUser(applyPreferredLanguageToUser(user, preferredLang));
      }
      await refetchAfterLanguageChange(queryClient);
      await Swal.fire({
        icon: "success",
        title: "Language updated",
        text: result?.meta?.message || "User language updated successfully!",
        confirmButtonColor: "#6c4de6",
      });
    },
    onError: async (mutationError) => {
      await Swal.fire({
        icon: "error",
        title: "Language update failed",
        text: mutationError?.message || "Unable to update language right now.",
        confirmButtonColor: "#6c4de6",
      });
    },
  });

  const handleLanguageToggle = async () => {
    if (!userId || storeLanguageMutation.isPending) {
      return;
    }

    const nextLanguage = currentPreferredLanguage === "HI" ? "EN" : "HI";
    await storeLanguageMutation.mutateAsync({
      user_id: Number(userId),
      preferred_lang: nextLanguage,
    });
  };

  return {
    currentPreferredLanguage,
    handleLanguageToggle,
    isPending: storeLanguageMutation.isPending,
  };
}